import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  grantPermissionToRole,
  listPermissionCatalog,
  revokePermissionFromRole,
} from '../../api/roles';
import type { GrantedPermission, PermissionCatalogEntry, RoleDetail } from '../../api/roles';

/** Giá trị của biểu mẫu cấp quyền. */
interface GrantFormValues {
  readonly permissionId: string;
  readonly grantReason: string;
}

/** Tham số của hộp quản lý quyền của vai trò. */
export interface RolePermissionsModalProps {
  /** Vai trò đang quản lý quyền, hoặc rỗng để đóng hộp. */
  readonly role: RoleDetail | null;

  /** Người đang đăng nhập có quyền cấp và thu hồi quyền. */
  readonly canManage: boolean;

  /** Nhận vai trò sau khi quyền thay đổi. */
  readonly onChanged: (updated: RoleDetail) => void;

  /** Đóng hộp. */
  readonly onClose: () => void;
}

/**
 * Hộp cấp và thu hồi quyền của một vai trò.
 *
 * **Quyền quyết định người dùng LÀM được những gì**, còn quy tắc phạm vi quyết định họ THẤY được những
 * dòng nào. Hai lớp tách biệt, và thiếu một trong hai thì màn hình hiện ra nhưng không có dữ liệu.
 *
 * Ba điều giao diện phải nói rõ vì chúng gây hiểu sai:
 *
 * 1. **Quyền nhạy cảm bắt buộc có lý do**, và lý do phải là một câu — máy chủ cưỡng chế độ dài tối
 *    thiểu. Ô lý do đổi thành bắt buộc ngay khi người dùng chọn một quyền nhạy cảm.
 * 2. **Thay đổi có hiệu lực ngay ở yêu cầu tiếp theo** của mọi người đang giữ vai trò. Không cần buộc
 *    họ đăng nhập lại.
 * 3. **Vai trò hệ thống không cấp hay thu hồi quyền được.** Quyền của chúng đồng bộ từ mã nguồn ở mỗi
 *    lần khởi động, nên mọi thay đổi bằng tay sẽ bị hoàn nguyên âm thầm.
 */
export function RolePermissionsModal({
  role,
  canManage,
  onChanged,
  onClose,
}: RolePermissionsModalProps): ReactNode {
  const [form] = Form.useForm<GrantFormValues>();

  const [catalog, setCatalog] = useState<readonly PermissionCatalogEntry[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [selectedPermissionId, setSelectedPermissionId] = useState<string | null>(null);

  const isOpen = role !== null;
  const isSystemRole = role?.role.isSystemRole ?? false;
  const canEdit = canManage && !isSystemRole;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const controller = new AbortController();

    void (async () => {
      try {
        const loaded = await listPermissionCatalog();

        if (!controller.signal.aborted) {
          setCatalog(loaded);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          void message.error(
            error instanceof ApiError ? error.message : 'Không nạp được danh mục quyền.',
          );
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [isOpen]);

  /*
    Chỉ hiện những quyền vai trò CHƯA có, và bỏ quyền đã lỗi thời. Hiện quyền đã có thì người dùng chọn
    vào rồi nhận lỗi trùng; hiện quyền lỗi thời thì họ chọn vào rồi nhận lỗi khác — cả hai đều là những
    lần bấm vô ích mà giao diện biết trước kết quả.
  */
  const grantableOptions = useMemo(() => {
    const alreadyGranted = new Set(
      (role?.permissions ?? []).map((permission) => permission.permissionId),
    );

    return catalog
      .filter((entry) => !entry.isObsolete && !alreadyGranted.has(entry.id))
      .map((entry) => ({
        value: entry.id,
        label: `${entry.code} — ${entry.displayName}`,
      }));
  }, [catalog, role]);

  /** Quyền đang chọn có phải quyền nhạy cảm không. */
  const selectedIsSensitive = useMemo(() => {
    if (selectedPermissionId === null) {
      return false;
    }

    return catalog.find((entry) => entry.id === selectedPermissionId)?.isSensitive ?? false;
  }, [catalog, selectedPermissionId]);

  async function handleGrant(values: GrantFormValues): Promise<void> {
    if (role === null) {
      return;
    }

    setIsBusy(true);

    try {
      const updated = await grantPermissionToRole(
        role.role.id,
        values.permissionId,
        values.grantReason === '' ? null : values.grantReason,
      );

      void message.success('Đã cấp quyền.');
      form.resetFields();
      setSelectedPermissionId(null);
      onChanged(updated);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không cấp được quyền.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function handleRevoke(permission: GrantedPermission): Promise<void> {
    if (role === null) {
      return;
    }

    setIsBusy(true);

    try {
      const updated = await revokePermissionFromRole(role.role.id, permission.permissionId);

      void message.success('Đã thu hồi quyền.');
      onChanged(updated);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không thu hồi được quyền.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  const columns: TableColumnsType<GrantedPermission> = [
    {
      title: 'Mã quyền',
      dataIndex: 'code',
      width: 280,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    { title: 'Tên quyền', dataIndex: 'displayName' },
    {
      title: '',
      key: 'flags',
      width: 190,
      render: (_value: unknown, permission: GrantedPermission) => (
        <Space size={4} wrap>
          {permission.isSensitive && (
            <Tooltip title={permission.grantReason ?? 'Không có lý do đã ghi.'}>
              <Tag color="orange">Nhạy cảm</Tag>
            </Tooltip>
          )}

          {permission.requiresTwoFactor && <Tag color="magenta">Cần 2 lớp</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_value: unknown, permission: GrantedPermission) =>
        canEdit ? (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={isBusy}
            onClick={() => {
              void handleRevoke(permission);
            }}
          >
            Thu hồi
          </Button>
        ) : null,
    },
  ];

  return (
    <Modal
      title={role === null ? 'Quyền của vai trò' : `Quyền của vai trò ${role.role.code}`}
      open={isOpen}
      width={860}
      footer={<Button onClick={onClose}>Đóng</Button>}
      onCancel={onClose}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {isSystemRole && (
          <Alert
            type="warning"
            showIcon
            message="Vai trò hệ thống không cấp hay thu hồi quyền được"
            description={
              'Quyền của vai trò hệ thống được đồng bộ từ mã nguồn ở mỗi lần khởi động, nên mọi thay '
              + 'đổi bằng tay sẽ bị hoàn nguyên. Cần một bộ quyền khác thì lập vai trò mới.'
            }
          />
        )}

        {!isSystemRole && (
          <Alert
            type="info"
            showIcon
            message="Thay đổi có hiệu lực ngay ở yêu cầu tiếp theo"
            description={
              'Mọi người đang giữ vai trò này nhận quyền mới hoặc mất quyền cũ ngay, không cần đăng '
              + 'nhập lại. Quyền quyết định họ LÀM được những gì; việc họ THẤY được những dòng nào do '
              + 'quy tắc phạm vi dữ liệu quyết định.'
            }
          />
        )}

        <Table<GrantedPermission>
          rowKey="permissionId"
          size="small"
          columns={columns}
          dataSource={[...(role?.permissions ?? [])]}
          pagination={false}
          scroll={{ y: 320 }}
          locale={{ emptyText: 'Vai trò chưa được cấp quyền nào.' }}
        />

        {canEdit && (
          <Form<GrantFormValues>
            form={form}
            layout="vertical"
            requiredMark={false}
            disabled={isBusy}
            onFinish={(values) => {
              void handleGrant(values);
            }}
          >
            <Form.Item
              name="permissionId"
              label="Cấp thêm quyền"
              extra="Chỉ hiện những quyền vai trò chưa có. Gõ để tìm theo mã hoặc theo tên."
              rules={[{ required: true, message: 'Chưa chọn quyền.' }]}
            >
              <Select<string>
                showSearch
                optionFilterProp="label"
                placeholder="Chọn quyền"
                options={grantableOptions}
                onChange={(value) => {
                  setSelectedPermissionId(value);
                }}
              />
            </Form.Item>

            <Form.Item
              name="grantReason"
              label="Lý do cấp quyền"
              extra={
                selectedIsSensitive
                  ? 'BẮT BUỘC vì đây là quyền nhạy cảm. Phải là một câu có nghĩa — đọc lại sau một năm vẫn phải hiểu được vì sao vai trò này có quyền đó.'
                  : 'Không bắt buộc với quyền thường, nhưng nên ghi.'
              }
              rules={
                selectedIsSensitive
                  ? [{ required: true, message: 'Quyền nhạy cảm bắt buộc ghi lý do.' }]
                  : []
              }
            >
              <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={isBusy}>
                Cấp quyền
              </Button>
            </Form.Item>
          </Form>
        )}
      </Space>
    </Modal>
  );
}
